// Capability registry — the frontend router seam. The server classifies each message into an
// `intent_kind`; the shell dispatches the post-interpret handling to the matching capability here.
// The filter path (`filter_query`) is still handled inline in the dashboardFilters orchestrator (its
// resolve/candidate pipeline is large); charts register their grounded-proposal builder. As more
// capabilities land, dispatch moves entirely through this table (see .mex scaling-plan).
import { buildChartProposal } from "./capabilities/dashboardCharts/chartProposal";
import { buildResetProposal } from "./capabilities/dashboardReset/resetProposal";
import { buildNavProposal } from "./capabilities/dashboardNavigation/navProposal";
import { buildTableProposal } from "./capabilities/tableView/tableProposal";
import { buildPlanProposal } from "./capabilities/planner/planProposal";
import { buildQaProposal } from "./capabilities/platformQa/qaProposal";
import { buildStateProposal } from "./capabilities/dashboardFilters/stateProposal";

const HANDLERS = {
    chart_command: { buildProposal: buildChartProposal },
    dashboard_reset: { buildProposal: buildResetProposal },
    navigate: { buildProposal: buildNavProposal },
    table_command: { buildProposal: buildTableProposal },
    // Platform Q&A: the server answered a "how does the platform work / where is X" question; the proposal
    // labels its citations and grounds its deep_links against the reachable nav catalog (same navTargets the
    // navigate kind gets), so any offered "jump there" link only points at a page the user can open.
    platform_qa: { buildProposal: buildQaProposal },
    // The multi-task planner: the server decomposed the message into ordered steps; the proposal validates
    // them against this mount's reachable pages and renders the always-confirm plan card.
    multi_task: { buildProposal: buildPlanProposal },
    // "Which filters are active?" — a read of the CLIENT's own state. The server only routes (no payload,
    // no second LLM call); this builder composes the answer from the live applied-filter summary the
    // orchestrator hands it. Read-only: nothing to confirm or apply.
    state_query: { buildProposal: buildStateProposal }
};

// The kinds that deliberately have NO handler — they ride the default (filter) path. Exported as a
// literal so the xrepo-route-kinds gate can prove every backend RouteKind is either handled above or
// declared here; an undeclared kind degrades to the generic fall-through bubble (the platform_qa-skew
// incident class), which masks drift as "Otto got dumber".
export const DEFAULT_PATH_KINDS = ["filter_query", "smalltalk", "unsupported"];

// Returns the capability handler for a router intent_kind, or null when the default (filter) path
// should handle it (see DEFAULT_PATH_KINDS).
export const handlerForKind = kind => HANDLERS[kind] || null;
